/**
 * Subscription / Quota module
 *
 * Centralizes plan tiers, monthly quotas, and feature gating logic.
 * The UI consumes this through the authStore (which hydrates plan + usage)
 * and the shared PaywallModal.
 */
import apiClient from './apiClient';
import { useAuthStore } from './authStore';

export type PlanId = 'free' | 'pro' | 'proPlus' | 'enterprise';

export interface UsageState {
  queriesThisMonth: number;
  hoursSaved: number;
  tasksCompleted: number;
  voiceChatsThisMonth: number;
  studioChatsThisMonth: number;
  usageMonth: string;
}

export interface SubscriptionState {
  plan: PlanId;
  usage: UsageState;
}

export const QUOTAS = {
  starter: {
    lumiText: -1,
    lumiVoice: 2,
    fileUploads: -1,
    studioChat: 30,
    studioImageGen: 0,
    studioVideoGen: 0,
    exportDownload: -1,
    higgsfieldImage: 0,
    higgsfieldVideo: 0,
    teamDashboard: false,
  },
  pro: {
    lumiText: -1,
    lumiVoice: -1,
    fileUploads: -1,
    studioChat: -1,
    studioImageGen: -1,
    studioVideoGen: -1,
    exportDownload: -1,
    higgsfieldImage: -1,
    higgsfieldVideo: -1,
    teamDashboard: false,
  },
  proPlus: {
    lumiText: -1,
    lumiVoice: -1,
    fileUploads: -1,
    studioChat: -1,
    studioImageGen: -1,
    studioVideoGen: -1,
    exportDownload: -1,
    higgsfieldImage: -1,
    higgsfieldVideo: -1,
    teamDashboard: true,
  },
} as const;

export type FeatureKey = keyof typeof QUOTAS.starter;

const PLAN_KEY_MAP: Record<PlanId, keyof typeof QUOTAS> = {
  free: 'starter',
  pro: 'pro',
  proPlus: 'proPlus',
  enterprise: 'proPlus',
};

export function getQuotaForFeature(plan: PlanId, feature: FeatureKey): number | boolean {
  const key = PLAN_KEY_MAP[plan] ?? 'starter';
  return QUOTAS[key][feature];
}

export function hasFeature(plan: PlanId, feature: FeatureKey): boolean {
  const quota = getQuotaForFeature(plan, feature);
  return quota === -1 || quota === true;
}

export function getFeatureLimit(plan: PlanId, feature: FeatureKey): number | null {
  const quota = getQuotaForFeature(plan, feature);
  if (typeof quota === 'boolean') return null;
  return quota;
}

export function getRemaining(
  plan: PlanId,
  feature: 'lumiVoice' | 'studioChat',
  usage: UsageState
): number | null {
  const limit = getFeatureLimit(plan, feature);
  if (limit === null) return null;
  const used = feature === 'lumiVoice' ? usage.voiceChatsThisMonth : usage.studioChatsThisMonth;
  return Math.max(0, limit - used);
}

export function isAtQuota(
  plan: PlanId,
  feature: 'lumiVoice' | 'studioChat',
  usage: UsageState
): boolean {
  const remaining = getRemaining(plan, feature, usage);
  return remaining !== null && remaining <= 0;
}

export async function refreshBillingState(): Promise<SubscriptionState | null> {
  const response = await apiClient.getBillingStatus();
  if (!response.success || !response.data) return null;
  return {
    plan: response.data.plan,
    usage: response.data.usage,
  };
}

export async function incrementUsage(feature: 'voiceChat' | 'studioChat'): Promise<UsageState | null> {
  const response = await apiClient.incrementBillingUsage(feature);
  if (!response.success || !response.data) return null;
  return response.data.usage;
}

export function planDisplayName(plan: PlanId): string {
  switch (plan) {
    case 'free':
      return 'Starter';
    case 'pro':
      return 'Pro';
    case 'proPlus':
      return 'Pro+';
    case 'enterprise':
      return 'Enterprise';
  }
}

export function requiredPlanForFeature(feature: FeatureKey): PlanId {
  if (feature === 'teamDashboard') return 'proPlus';
  if (
    feature === 'higgsfieldImage' ||
    feature === 'higgsfieldVideo' ||
    feature === 'studioImageGen' ||
    feature === 'studioVideoGen'
  ) {
    return 'pro';
  }
  if (feature === 'lumiVoice' || feature === 'studioChat') return 'pro';
  return 'free';
}

export function useSubscription(): SubscriptionState {
  const plan = useAuthStore((s) => (s.user as any)?.plan ?? 'free') as PlanId;
  const usage = useAuthStore((s) => (s.user as any)?.usage) as UsageState | undefined;
  return {
    plan,
    usage: usage ?? {
      queriesThisMonth: 0,
      hoursSaved: 0,
      tasksCompleted: 0,
      voiceChatsThisMonth: 0,
      studioChatsThisMonth: 0,
      usageMonth: new Date().toISOString().slice(0, 7),
    },
  };
}
